import * as React from "react";
import { useSearchParams } from "./searchParams";

type FilterDefaults = Record<
  string,
  string | number | boolean | Record<string, unknown>
>;

type FilterValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | null
  | undefined;

export default function useFilters<TDefaults extends FilterDefaults>({
  defaultValues,
  validate,
  modifyFilter,
}: {
  defaultValues: TDefaults;
  validate?: (params: TDefaults) => Record<string, boolean>;
  modifyFilter?: (name: string, value: unknown) => Record<string, FilterValue>;
}) {
  const {
    searchParams: filters,
    setSearchParams,
    removeParams,
  } = useSearchParams(defaultValues);

  const invalidKeys = React.useMemo(
    () =>
      Object.entries(validate?.(filters) ?? {})
        .filter(([, invalid]) => invalid)
        .map(([key]) => key),
    [filters, validate],
  );
  const isValid = !invalidKeys.length;

  React.useEffect(() => {
    if (!invalidKeys.length) return;
    removeParams(invalidKeys);
  }, [invalidKeys, removeParams]);

  const setFilter = React.useCallback(
    (name: string, value: unknown) => {
      setSearchParams(
        modifyFilter?.(name, value) ?? { [name]: value as FilterValue },
      );
    },
    [modifyFilter, setSearchParams],
  );

  return {
    filters,
    setFilter,
    setSearchParams,
    isValid,
  };
}
